/**
 * The "Custom prompt" card of the PDP image set picker (POR-15158): the user writes
 * the whole image prompt, as they would in the image tools, and gets an image of
 * their own product from it. Underneath it is a Lifestyle row, so it keeps that
 * path's product handling (the packshot goes in as the base image and is put back
 * afterwards); only the prompt differs — the user's leads instead of the type's
 * template, which would otherwise fight it (plain props, no text, product centered).
 *
 * No imports, so the picker, the generation paths and the tests can all load it.
 */

export const CUSTOM_PROMPT_LABEL = 'Custom prompt';

export function isCustomPromptRow(row) {
  return !!(row && row.customPrompt);
}

/** A Custom prompt card with nothing written yet has nothing to generate from. */
export function customPromptMissing(row) {
  return isCustomPromptRow(row) && !String(row.userInstructions || '').trim();
}

/** A new, empty Custom prompt row for the picker. */
export function newCustomPromptRow(rowIndex) {
  return {
    rowIndex,
    type: 'lifestyle',
    label: CUSTOM_PROMPT_LABEL,
    customPrompt: true,
    mode: 'ai',
    template_id: null,
    template_thumb: null,
    scene: null,
    productMode: null,
    userInstructions: '',
  };
}

/**
 * The image prompt for a Custom prompt card: the user's prompt, framed only by what
 * keeps their product their product.
 *
 *   baseIsProduct — the packshot is IMAGE 1, the base image the backend edits
 *   noProduct     — there is no product image: the prompt goes alone
 *   requirements  — rules that still apply (overlay text, brand logo, language)
 */
export function buildCustomPromptImagePrompt(userPrompt, { baseIsProduct = false, noProduct = false, requirements = [] } = {}) {
  const parts = ['Create the image described in the USER PROMPT below.'];

  if (!noProduct) {
    parts.push(
      `${baseIsProduct ? 'IMAGE 1 (the base image)' : 'The supplied product image'} is a photograph of the real product: show THAT exact product wherever the prompt puts it — same packaging shape and proportions, same colours, same brand logo and typography, same product name and every visible word on the label.`,
      'Do NOT redesign, reinterpret, simplify, recolour, relabel or distort it, and do NOT swap in a different product, variant or packaging.',
      'Place, angle, scale and light it the way the prompt describes (it may be held, tucked into a bag or partly covered); the plain background and framing of the product photo need not be kept — build the full scene the prompt asks for.',
      'Any side of the product the photo does not show stays plain in the packaging\'s own colour, with no invented lettering.',
    );
  }

  parts.push('Produce ONE single image: not a grid, a collage, split panels or several variations.');

  (requirements || [])
    .map((r) => String(r || '').trim())
    .filter(Boolean)
    .forEach((r) => parts.push(r));

  return `${parts.join(' ')}\n\nUSER PROMPT:\n${String(userPrompt || '').trim()}`;
}
